import { getApiBaseUrl } from '@/config/env';

/**
 * Normalizes stored file paths from the API (bare filenames, missing prefixes, legacy paths).
 */
export function normalizeStoredPath(path: string): string | undefined {
  const trimmed = path.trim();
  if (!trimmed) {
    return undefined;
  }
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:')) {
    const apiIdx = trimmed.indexOf('/api/');
    if (apiIdx >= 0) {
      return trimmed.slice(apiIdx + 4);
    }
    return trimmed;
  }
  const withoutQuery = trimmed.split('?')[0];
  if (
    withoutQuery.startsWith('/images/') ||
    withoutQuery.startsWith('/documents/') ||
    withoutQuery.startsWith('/files/')
  ) {
    return withoutQuery;
  }
  if (withoutQuery.startsWith('images/') || withoutQuery.startsWith('documents/') || withoutQuery.startsWith('files/')) {
    return `/${withoutQuery}`;
  }
  if (withoutQuery.startsWith('/')) {
    const name = withoutQuery.slice(1);
    return name.includes('/') ? withoutQuery : `/documents/${name}`;
  }
  return `/documents/${withoutQuery}`;
}

/** True when the stored path is under the public tenant branch (no auth required to read). */
export function isPublicStoredAssetPath(path: string | undefined | null): boolean {
  const normalized = path ? normalizeStoredPath(path) : undefined;
  return normalized != null && normalized.includes('/public/');
}

/**
 * Resolves API-relative file URLs for use in img/src/href (includes API base path).
 */
export function resolveFileUrl(path: string | undefined | null): string | undefined {
  const normalized = path ? normalizeStoredPath(path) : undefined;
  if (!normalized) {
    return undefined;
  }
  if (normalized.startsWith('http://') || normalized.startsWith('https://') || normalized.startsWith('data:')) {
    return normalized;
  }
  const base = getApiBaseUrl();
  return normalized.startsWith('/') ? `${base}${normalized}` : `${base}/${normalized}`;
}

/**
 * Public tenant assets — static /files/** serving without session.
 */
export function resolvePublicAssetUrl(path: string | undefined | null): string | undefined {
  if (!isPublicStoredAssetPath(path)) {
    return resolveFileContentUrl(path);
  }
  return resolveFileUrl(path);
}

/**
 * Picks static public URL or authenticated content URL based on path branch.
 */
export function resolveStoredAssetUrl(
  path: string | undefined | null,
  disposition: 'inline' | 'attachment' = 'inline',
): string | undefined {
  if (isPublicStoredAssetPath(path)) {
    return resolveFileUrl(path);
  }
  return resolveFileContentUrl(path, disposition);
}

/**
 * API-relative path for axios (session cookies / refresh), e.g. `/v1/files/content?...`.
 */
export function resolveFileContentRequestPath(
  path: string | undefined | null,
  disposition: 'inline' | 'attachment' = 'inline',
): string | undefined {
  const normalized = path ? normalizeStoredPath(path) : undefined;
  if (!normalized) {
    return undefined;
  }
  if (
    normalized.startsWith('http://') ||
    normalized.startsWith('https://') ||
    normalized.startsWith('data:')
  ) {
    return undefined;
  }
  if (isPublicStoredAssetPath(normalized)) {
    return normalized;
  }
  const params = new URLSearchParams({
    path: normalized,
    disposition,
  });
  return `/v1/files/content?${params.toString()}`;
}

/**
 * Authenticated content endpoint — reliable preview/download with session cookies.
 */
export function resolveFileContentUrl(path: string | undefined | null, disposition: 'inline' | 'attachment' = 'inline'): string | undefined {
  const requestPath = resolveFileContentRequestPath(path, disposition);
  if (!requestPath) {
    return resolveFileUrl(path);
  }
  if (isPublicStoredAssetPath(path)) {
    return resolveFileUrl(path);
  }
  const base = getApiBaseUrl();
  return requestPath.startsWith('/') ? `${base}${requestPath}` : `${base}/${requestPath}`;
}

/**
 * Profile photos are stored server-side; resolve a stable URL that works in any browser.
 * Uses GET /v1/public/profile-pictures/{userUuid} for uploaded and OAuth photos.
 */
export function resolveProfileImageUrl(
  path: string | null | undefined,
  userUuid?: string | null,
  cacheRevision?: number | null,
): string | undefined {
  const trimmed = path?.trim() ?? '';
  if (!trimmed) {
    return undefined;
  }
  if (trimmed.startsWith('data:')) {
    return trimmed;
  }
  if (userUuid) {
    const base = `${getApiBaseUrl()}/v1/public/profile-pictures/${userUuid}`;
    const cacheKey = cacheRevision != null ? String(cacheRevision) : trimmed;
    return `${base}?v=${encodeURIComponent(cacheKey)}`;
  }
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  return resolveFileUrl(trimmed);
}
